import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { PreArrivalFormFields } from '../PreArrivalFormTab.types';
import {
  ARRIVAL_INFORMATION_FIELDS,
  OTHER_GUEST_FIELDS,
} from '../PreArrivalFormTab.constants';
import {
  PreviewContainer,
  PreviewDivider,
  PreviewHeader,
} from './PreviewPreArrivalForm.styles';
import useShouldShowField from './useShouldShowField';
import PrimaryGuestInformationSection from './sections/PrimaryGuestInformationSection';
import OtherGuestInformationSection from './sections/OtherGuestInformationSection';
import HouseRulesSection from './sections/HouseRulesSection';
import ArrivalInformationSection from './sections/ArrivalInformationSection';
import AdditionalInformationSection from './sections/AdditionalInformationSection';

const PreviewPreArrivalForm = () => {
  const { t } = useTranslation();
  const { isFieldActive } = useShouldShowField();
  const { watch } = useFormContext();

  const shouldShowOtherGuestFields = () => {
    const isAnyOtherGuestFieldActive = OTHER_GUEST_FIELDS.some((field) => {
      const value = watch(field);
      return Boolean(value?.isActive);
    });

    const isNumberOfGuestsActive = isFieldActive(
      PreArrivalFormFields.PRIMARY_GUEST_NUMBER_OF_GUESTS,
    );

    const isNumberOfGuestsGreaterThanOne = watch('preview.howManyGuests') > 1;

    return (
      isAnyOtherGuestFieldActive &&
      isNumberOfGuestsActive &&
      isNumberOfGuestsGreaterThanOne
    );
  };

  const shouldShowArrivalInformationFields = useMemo(() => {
    return ARRIVAL_INFORMATION_FIELDS.some((field) => {
      const value = watch(field);
      return Boolean(value?.isActive);
    });
  }, [watch]);

  const shouldShowHouseRules = useMemo(() => {
    return isFieldActive(PreArrivalFormFields.OTHER_AGREE_HOUSE_RULES);
  }, [isFieldActive]);

  return (
    <PreviewContainer data-testid="preview-pre-arrival-form">
      <PreviewDivider />

      <PreviewHeader>
        <h2>
          {t('pageAutomations.preArrivalForm.preview.header.planningYourTrip')}
        </h2>
        <h3>
          {t(
            'pageAutomations.preArrivalForm.preview.header.pleaseProvideAdditionalInformation',
          )}
        </h3>
      </PreviewHeader>

      <PreviewDivider />

      <PrimaryGuestInformationSection />

      <PreviewDivider />

      {shouldShowOtherGuestFields() && <OtherGuestInformationSection />}

      {shouldShowArrivalInformationFields && <ArrivalInformationSection />}

      {shouldShowHouseRules && <HouseRulesSection />}

      <AdditionalInformationSection />
    </PreviewContainer>
  );
};

export default PreviewPreArrivalForm;
